import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import BulkActionsAPI from 'dashboard/api/bulkActions';

/**
 * Composable for bulk label removal logic
 * @param {string} entityType - Either 'Contact' or 'Conversation'
 * @returns {Object} An object containing the removeLabels function
 */
export function useBulkLabelRemoval(entityType) {
  const store = useStore();
  const { t } = useI18n();

  /**
   * Removes labels from multiple entities
   * @param {Array<number|string>} ids - Array of entity IDs
   * @param {Array<string>} labels - Array of label titles to remove
   * @param {Function} onSuccess - Callback function to execute on success
   * @returns {Promise<void>}
   */
  const removeLabels = async (ids, labels, onSuccess) => {
    if (!ids || ids.length === 0 || !labels || labels.length === 0) {
      return;
    }

    try {
      if (entityType === 'Contact') {
        const response = await BulkActionsAPI.create({
          type: 'Contact',
          ids,
          labels: { remove: labels },
        });
        useAlert(t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));

        // Update the store with response data
        if (response?.data?.updated_items) {
          store.dispatch(
            'contactLabels/bulkUpdateLabels',
            response.data.updated_items
          );
        }
      } else {
        // Conversation
        const response = await BulkActionsAPI.create({
          type: 'Conversation',
          ids,
          labels: { remove: labels },
        });
        useAlert(t('BULK_ACTION.LABELS.REMOVE_SUCCESFUL'));

        // Update the store with response data
        if (response?.data?.updated_items) {
          store.dispatch(
            'conversationLabels/bulkUpdateLabels',
            response.data.updated_items
          );
        }
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorKey =
        entityType === 'Contact'
          ? 'CONTACTS_BULK_ACTIONS.REMOVE_LABELS_FAILED'
          : 'BULK_ACTION.LABELS.REMOVE_FAILED';
      useAlert(t(errorKey));
      throw error;
    }
  };

  return { removeLabels };
}
